import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import {
  Container, Typography, TextField, Button, Box, Grid,
  MenuItem, Alert, CircularProgress, Card, CardContent,
  Stepper, Step, StepLabel
} from '@mui/material';
import { PersonAdd, CheckCircle } from '@mui/icons-material';
import axios from 'axios';
import Cookies from 'js-cookie';

const API_BASE = API_BASE_URL;

const AdminDataEntryPage = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(true);

  // Dynamic form options
  const [formOptions, setFormOptions] = useState({
    schools: {},
    courseLevels: [],
    honors: [],
    countries: []
  });

  // Emphasis state
  const [selectedSchool, setSelectedSchool] = useState('');
  const [selectedDegree, setSelectedDegree] = useState('');
  const [availableEmphases, setAvailableEmphases] = useState([]);
  const [selectedEmphasis, setSelectedEmphasis] = useState('');
  const [otherEmphasisText, setOtherEmphasisText] = useState('');

  const [formData, setFormData] = useState({
    First_Name: '',
    Last_Name: '',
    Gender: '',
    'Student Id': '',
    School: '',
    'Degree Obtained': '',
    'Course Level': '',
    graduation: '',
    Honors: '',
    Department: '',
    'Other Degree Attained': '',
    'Email Contact': '',
    'USC Email': '',
    'Contact Number': '',
    'Alternative Phone Number': '',
    Address: '',
    Country: 'Trinidad and Tobago',
    'Current Job Title': '',
    Attending: '',
    'Referred By': ''
  });

  const steps = ['Basic Information', 'Academic Details', 'Contact Information'];
  const GENDERS = ["Male", "Female"];

  useEffect(() => {
    fetchFormOptions();
  }, []);

  const fetchFormOptions = async () => {
    try {
      const response = await axios.get(`${API_BASE}/admin/form-options`);
      setFormOptions(response.data);
    } catch (err) {
      console.error('Error fetching form options:', err);
      setError('Failed to load form options');
    } finally {
      setOptionsLoading(false);
    }
  };

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
    setError('');
  };

  const handleSchoolChange = (school) => {
    setSelectedSchool(school);
    setSelectedDegree('');
    setSelectedEmphasis('');
    setAvailableEmphases([]);
    setOtherEmphasisText('');
    setFormData({
      ...formData,
      School: school,
      'Degree Obtained': ''
    });
  };

  const handleDegreeChange = (degree) => {
    setSelectedDegree(degree);
    setSelectedEmphasis('');
    setOtherEmphasisText('');

    // Use degree name directly - Firebase keys have periods and spaces
    const emphases = formOptions.schools[selectedSchool]?.emphases?.[degree] || [];
    setAvailableEmphases(emphases);

    // Update form data with just degree (no emphasis yet)
    setFormData({
      ...formData,
      'Degree Obtained': degree
    });
  };

  const handleEmphasisChange = (emphasis) => {
    setSelectedEmphasis(emphasis);

    if (emphasis === 'Other') {
      setOtherEmphasisText('');
      setFormData({
        ...formData,
        'Degree Obtained': selectedDegree
      });
    } else if (emphasis) {
      // Combine degree + emphasis
      setOtherEmphasisText('');
      setFormData({
        ...formData,
        'Degree Obtained': `${selectedDegree} - ${emphasis}`
      });
    } else {
      // No emphasis selected
      setOtherEmphasisText('');
      setFormData({
        ...formData,
        'Degree Obtained': selectedDegree
      });
    }
  };

  const handleOtherEmphasisChange = (text) => {
    setOtherEmphasisText(text);
    if (text.trim()) {
      setFormData({
        ...formData,
        'Degree Obtained': `${selectedDegree} - ${text}`
      });
    } else {
      setFormData({
        ...formData,
        'Degree Obtained': selectedDegree
      });
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!formData.First_Name || !formData.Last_Name || !formData.Gender) {
        setError('Please fill in all required fields');
        return;
      }
    } else if (activeStep === 1) {
      if (!formData.School || !formData['Degree Obtained'] || !formData['Course Level'] || !formData.graduation) {
        setError('Please fill in all required fields');
        return;
      }
      // Validate Other emphasis
      if (selectedEmphasis === 'Other' && !otherEmphasisText.trim()) {
        setError('Please specify the emphasis when selecting "Other"');
        return;
      }
    }

    setActiveStep((prev) => prev + 1);
    setError('');
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError('');
  };

  const handleSubmit = async () => {
    if (!formData['Email Contact'] || !formData['Contact Number'] || !formData.Country) {
      setError('Please fill in all required contact fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = Cookies.get('idToken');

      const submissionData = {
        ...formData,
        'FULL NAME': `${formData.First_Name} ${formData.Last_Name}`
      };

      await axios.post(`${API_BASE}/admin/alumni/add`, submissionData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess(true);

      setTimeout(() => {
        setFormData({
          First_Name: '',
          Last_Name: '',
          Gender: '',
          'Student Id': '',
          School: '',
          'Degree Obtained': '',
          'Course Level': '',
          graduation: '',
          Honors: '',
          Department: '',
          'Other Degree Attained': '',
          'Email Contact': '',
          'USC Email': '',
          'Contact Number': '',
          'Alternative Phone Number': '',
          Address: '',
          Country: 'Trinidad and Tobago',
          'Current Job Title': '',
          Attending: '',
          'Referred By': ''
        });
        setSelectedSchool('');
        setSelectedDegree('');
        setSelectedEmphasis('');
        setAvailableEmphases([]);
        setOtherEmphasisText('');
        setActiveStep(0);
        setSuccess(false);
      }, 3000);

    } catch (err) {
      console.error('Error adding alumni:', err);
      setError(err.response?.data?.error || 'Failed to add alumni');
    } finally {
      setLoading(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const YEARS = Array.from({ length: currentYear - 1926 }, (_, i) => currentYear - i);

  const degreeOptions = selectedSchool && formOptions.schools[selectedSchool]
    ? formOptions.schools[selectedSchool].degrees || []
    : [];

  if (optionsLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>Loading form...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <PersonAdd sx={{ fontSize: 40, color: 'primary.main' }} />
        <Box>
          <Typography variant="h4">Alumni Data Entry</Typography>
          <Typography variant="body2" color="text.secondary">
            Add alumni directly to the MasterList (Admin only)
          </Typography>
        </Box>
      </Box>

      {success ? (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="h6">✅ Alumni Added Successfully!</Typography>
          <Typography variant="body2">The alumni has been added directly to the MasterList.</Typography>
        </Alert>
      ) : (
        <>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Card>
            <CardContent>
              {/* Step 1: Basic Information */}
              {activeStep === 0 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="First Name"
                      value={formData.First_Name}
                      onChange={handleChange('First_Name')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Last Name"
                      value={formData.Last_Name}
                      onChange={handleChange('Last_Name')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      select
                      label="Gender"
                      value={formData.Gender}
                      onChange={handleChange('Gender')}
                    >
                      {GENDERS.map((gender) => (
                        <MenuItem key={gender} value={gender}>{gender}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Student ID"
                      value={formData['Student Id']}
                      onChange={handleChange('Student Id')}
                    />
                  </Grid>
                </Grid>
              )}

              {/* Step 2: Academic Details */}
              {activeStep === 1 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      select
                      label="School"
                      value={selectedSchool}
                      onChange={(e) => handleSchoolChange(e.target.value)}
                    >
                      {Object.keys(formOptions.schools).map((school) => (
                        <MenuItem key={school} value={school}>{school}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      select
                      label="Degree Obtained"
                      value={selectedDegree}
                      onChange={(e) => handleDegreeChange(e.target.value)}
                      disabled={!selectedSchool}
                      helperText={!selectedSchool ? "Select a school first" : ""}
                    >
                      {degreeOptions.map((degree) => (
                        <MenuItem key={degree} value={degree}>{degree}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Emphasis dropdown - only shows if degree has emphases */}
                  {availableEmphases.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        select
                        label="Emphasis/Concentration (Optional)"
                        value={selectedEmphasis}
                        onChange={(e) => handleEmphasisChange(e.target.value)}
                      >
                        <MenuItem value="">None</MenuItem>
                        {availableEmphases.map((emphasis) => (
                          <MenuItem key={emphasis} value={emphasis}>{emphasis}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}

                  {/* Other text field - only shows if "Other" is selected */}
                  {selectedEmphasis === 'Other' && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        required
                        label="Specify Other Emphasis"
                        value={otherEmphasisText}
                        onChange={(e) => handleOtherEmphasisChange(e.target.value)}
                        placeholder="e.g., Environmental Studies"
                        helperText="Enter the specific emphasis/concentration"
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      select
                      label="Course Level"
                      value={formData['Course Level']}
                      onChange={handleChange('Course Level')}
                    >
                      {formOptions.courseLevels.map((level) => (
                        <MenuItem key={level} value={level}>{level}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      select
                      label="Graduation Year"
                      value={formData.graduation}
                      onChange={handleChange('graduation')}
                    >
                      {YEARS.map((year) => (
                        <MenuItem key={year} value={year}>{year}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      select
                      label="Honors"
                      value={formData.Honors}
                      onChange={handleChange('Honors')}
                    >
                      {formOptions.honors.map((honor) => (
                        <MenuItem key={honor} value={honor}>{honor || "None"}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Department"
                      value={formData.Department}
                      onChange={handleChange('Department')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Other Degrees Attained"
                      value={formData['Other Degree Attained']}
                      onChange={handleChange('Other Degree Attained')}
                    />
                  </Grid>
                </Grid>
              )}

              {/* Step 3: Contact Information */}
              {activeStep === 2 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Email Contact"
                      type="email"
                      value={formData['Email Contact']}
                      onChange={handleChange('Email Contact')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="USC Email"
                      type="email"
                      value={formData['USC Email']}
                      onChange={handleChange('USC Email')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Contact Number"
                      value={formData['Contact Number']}
                      onChange={handleChange('Contact Number')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Alternative Phone Number"
                      value={formData['Alternative Phone Number']}
                      onChange={handleChange('Alternative Phone Number')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address"
                      multiline
                      rows={2}
                      value={formData.Address}
                      onChange={handleChange('Address')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      select
                      label="Country"
                      value={formData.Country}
                      onChange={handleChange('Country')}
                    >
                      {formOptions.countries.map((country) => (
                        <MenuItem key={country} value={country}>{country}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Current Job Title"
                      value={formData['Current Job Title']}
                      onChange={handleChange('Current Job Title')}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Attending"
                      value={formData.Attending}
                      onChange={handleChange('Attending')}
                      helperText="Current educational institution (if applicable)"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Referred By (Optional)"
                      value={formData['Referred By']}
                      onChange={handleChange('Referred By')}
                      placeholder="Name of person who referred this alumni"
                      helperText="Track referrals for analytics"
                    />
                  </Grid>
                </Grid>
              )}

              {/* Navigation Buttons */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button disabled={activeStep === 0} onClick={handleBack}>
                  Back
                </Button>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {activeStep < steps.length - 1 ? (
                    <Button variant="contained" onClick={handleNext}>
                      Next
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
                    >
                      {loading ? 'Adding...' : 'Add to MasterList'}
                    </Button>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </Container> 
  );
};

export default AdminDataEntryPage;